import { useCallback, useEffect, useRef, useState } from "react";
import type { ApiClient } from "./api";

/**
 * How much of a turn a sample plays before it stops itself.
 *
 * A voice is recognised in a few seconds, and the Speakers tab's play button is for
 * recognising a voice — not for listening to a four-minute answer somebody gave. The
 * handoff's fragment player writes its label as "09:47 · 0:06", which is the same idea.
 */
export const MAX_SAMPLE_MS = 8000;

/** `HTMLMediaElement.HAVE_METADATA` — the point at which a seek will be honoured. */
const HAVE_METADATA = 1;

export interface AudioFragment {
  /** Something to identify what is playing, so the right button shows as playing. */
  id: string;
  startMs: number;
  endMs: number | null;
}

export interface FragmentPlayer {
  /** The id of the fragment currently playing, or null. */
  playing: string | null;
  /**
   * How far through the playing fragment the audio element actually is, 0 to 1.
   *
   * The handoff's waveform fills as the clip plays, and this is what fills it — the
   * element's own `currentTime` against the fragment's own bounds. UI-16 is explicit that
   * it must not be a timer counting alongside the sound: a clip that buffers, or one the
   * browser refuses outright, would leave a bar chart confidently reporting progress
   * through silence. Zero whenever nothing is playing.
   */
  progress: number;
  play: (fragment: AudioFragment, maxMs?: number) => void;
  stop: () => void;
  /** Why nothing can be played, or null. */
  problem: string | null;
}

/**
 * SEC-4's link, put to work: playing a few seconds from a point in a recording.
 *
 * The link is asked for once when the recording is opened and reused for the rest of
 * the visit, which is exactly what SEC-4 describes — one listening session, many
 * fragments, one short-lived credential. Asking per fragment would mint a credential
 * per click for no gain, since the link is scoped to this recording and this listener
 * either way.
 *
 * One `Audio` element for the whole page, held in a ref. Several would each hold their
 * own connection to a file that can be four gigabytes, and two of them would play at
 * once the first time somebody clicked a second speaker.
 *
 * **The seek has to wait for metadata.** Setting `currentTime` on an element that has
 * not loaded any yet is discarded — so the obvious code (set `src`, set `currentTime`,
 * `play()`) plays the *beginning of the meeting* on the first click of a visit and the
 * right fragment on every one after, which is a bug that hides behind its own
 * inconsistency. Nothing starts until `loadedmetadata` has been seen at least once.
 */
export function useAudioFragment(api: ApiClient, recordingId: string | undefined): FragmentPlayer {
  const audio = useRef<HTMLAudioElement | null>(null);
  const loaded = useRef<string | null>(null);
  const stopAt = useRef<number | null>(null);
  // Which click is current. A second click while the first is still waiting for
  // metadata must not leave two pending starts racing each other.
  const generation = useRef(0);

  const [source, setSource] = useState<string | null>(null);
  const [playing, setPlaying] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [problem, setProblem] = useState<string | null>(null);

  // A fresh link each time a recording is opened, per SEC-4 — which keeps the window
  // in which any given link is worth anything down to one visit.
  useEffect(() => {
    if (!recordingId) return;
    let cancelled = false;

    setSource(null);
    setProblem(null);

    void api
      .createAudioLink(recordingId)
      .then((link) => {
        if (!cancelled) setSource(link.url);
      })
      .catch(() => {
        if (!cancelled) setProblem("The audio for this recording could not be opened.");
      });

    return () => {
      cancelled = true;
    };
  }, [api, recordingId]);

  const stop = useCallback(() => {
    generation.current += 1;
    audio.current?.pause();
    stopAt.current = null;
    setPlaying(null);
    setProgress(0);
  }, []);

  // Leaving the page has to stop the sound. Without this the element outlives the
  // component and keeps playing over whatever the owner navigated to.
  useEffect(() => stop, [stop]);

  const play = useCallback(
    (fragment: AudioFragment, maxMs: number = MAX_SAMPLE_MS) => {
      if (!source) return;

      if (playing === fragment.id) {
        stop();
        return;
      }

      const element = (audio.current ??= new Audio());

      generation.current += 1;
      const mine = generation.current;

      if (loaded.current !== source) {
        loaded.current = source;
        element.preload = "metadata";
        element.src = source;
      }

      // Bounded twice: by the end of the turn, and by what a sample is for. A turn
      // that runs for four minutes is still an eight-second sample, unless the
      // caller asks for a longer one (maxMs).
      const end = Math.min(fragment.endMs ?? Number.POSITIVE_INFINITY, fragment.startMs + maxMs);

      const start = () => {
        if (generation.current !== mine) return;

        stopAt.current = end / 1000;
        element.currentTime = fragment.startMs / 1000;

        const from = fragment.startMs / 1000;
        const span = Math.max(end / 1000 - from, 0.001);

        element.ontimeupdate = () => {
          setProgress(Math.min(Math.max((element.currentTime - from) / span, 0), 1));
          if (stopAt.current !== null && element.currentTime >= stopAt.current) stop();
        };

        element.onended = () => stop();

        // jsdom has no media stack and a browser may refuse playback; either way the
        // button must not be left claiming to play.
        void Promise.resolve(element.play()).catch(() => {
          if (generation.current === mine) setPlaying(null);
          setProblem("This browser would not play the recording.");
        });
      };

      setPlaying(fragment.id);
      setProgress(0);

      if (element.readyState >= HAVE_METADATA) start();
      else element.addEventListener("loadedmetadata", start, { once: true });
    },
    [playing, source, stop],
  );

  return { playing, progress, play, stop, problem };
}
